"use client";

import type { DeviceId } from "@/components/landing/ResponsiveShowcase/types";
import { motion, useReducedMotion } from "framer-motion";
import { DEVICES, MORPH_EASE } from "@/components/landing/ResponsiveShowcase/morphAnimation";
import { Glyph } from "@/components/landing/shared";
import { cn } from "@/lib/utils";

interface DeviceSwitcherProps {
	title: string;
	active: DeviceId;
	deviceLabels: Record<DeviceId, string>;
	onChange: (id: DeviceId) => void;
}

function DeviceSwitcher(prop: DeviceSwitcherProps) {
	const { title, active, deviceLabels, onChange } = prop;
	const reduceMotion = useReducedMotion();

	return (
		<div
			role="tablist"
			aria-label={title}
			className="inline-flex items-center gap-1 rounded-full border border-border bg-surface/60 p-1.5"
		>
			{DEVICES.map((d) => {
				const selected = active === d.id;
				return (
					<button
						key={d.id}
						type="button"
						role="tab"
						aria-selected={selected}
						onClick={() => onChange(d.id)}
						className={cn(
							"relative flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-1)] focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
							selected ? "text-white" : "text-muted-foreground hover:text-text",
						)}
					>
						{/* One shared indicator slides between tabs via layout animation. */}
						{selected && (
							<motion.span
								layoutId="showcase-tab-indicator"
								aria-hidden
								className="absolute inset-0 rounded-full"
								style={{
									background: "linear-gradient(135deg, var(--brand-1), var(--brand-2))",
								}}
								transition={reduceMotion ? { duration: 0 } : { duration: 0.24, ease: MORPH_EASE }}
							/>
						)}
						<Glyph name={d.icon} className="relative z-10 h-4 w-4" />
						<span className="relative z-10">{deviceLabels[d.id]}</span>
					</button>
				);
			})}
		</div>
	);
}

export default DeviceSwitcher;
